import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import toast from 'react-hot-toast';

const LearningItemView = () => {
    const { itemId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [item, setItem] = useState(null);
    const [assignment, setAssignment] = useState(null);
    const [progress, setProgress] = useState(0);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadItem();
    }, [itemId]);

    const loadItem = async () => {
        try {
            const response = await axios.get(`/learning/item/${itemId}`);
            setItem(response.data.data.item);
            setAssignment(response.data.data.assignment);
            setProgress(response.data.data.assignment?.progress_percentage || 0);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load learning item');
            navigate('/learning');
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        try {
            await axios.post(`/learning/item/${itemId}/start`);
            loadItem();
            toast.success('Started learning item!');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to start');
        }
    };

    const handleComplete = async () => {
        try {
            await axios.post(`/learning/item/${itemId}/complete`);
            toast.success('🎉 Item completed!');
            navigate('/learning');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to complete');
        }
    };

    const handleQuizSubmit = async () => {
        if (!item.knowledgeCheck) return;

        setSubmitting(true);
        try {
            const response = await axios.post(
                `/learning/knowledge-check/${item.knowledgeCheck.id}/submit`,
                { answers: quizAnswers }
            );

            const result = response.data.data;
            if (result.passed) {
                toast.success(result.message);
                navigate('/learning');
            } else {
                toast.error(result.message);
                setQuizAnswers({});
                setShowQuiz(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit quiz');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="text-center py-8 text-gray-500">
                Item not found
            </div>
        );
    }

    const isLocked = assignment?.status === 'locked';
    const isAvailable = assignment?.status === 'available';
    const isInProgress = assignment?.status === 'in_progress';
    const isCompleted = assignment?.status === 'completed';
    const hasKnowledgeCheck = !!item.knowledgeCheck;

    return (
        <div className="w-full">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center space-x-2">
                                <span className="text-3xl">{item.getIcon?.() || '📌'}</span>
                                <h1 className="text-2xl font-semibold text-gray-900">
                                    {item.title}
                                </h1>
                            </div>
                            <p className="text-gray-600 mt-2">{item.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                                {item.type?.replace(/_/g, ' ') || 'Module'}
                            </span>
                            <span className="text-sm text-gray-300">•</span>
                            <span className="text-sm text-gray-500">
                                {item.required_hours} hrs
                            </span>
                            {isCompleted && (
                                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    ✅ Complete
                                </span>
                            )}
                        </div>
                    </div>

                    {isInProgress && (
                        <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>Progress</span>
                                <span>{progress}%</span>
                            </div>
                            <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-black transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 space-y-6">
                    {item.prerequisites && item.prerequisites.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Prerequisites</h3>
                            <div className="space-y-1">
                                {item.prerequisites.map(p => (
                                    <div key={p.id} className="flex items-center space-x-2 text-sm">
                                        <span className={p.completed ? 'text-green-600' : 'text-gray-400'}>
                                            {p.completed ? '✅' : '🔒'}
                                        </span>
                                        <span className={p.completed ? 'text-gray-700' : 'text-gray-400'}>
                                            {p.title}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {item.resources && item.resources.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Resources</h3>
                            <div className="space-y-2">
                                {item.resources.map(resource => (
                                    <a
                                        key={resource.id}
                                        href={resource.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {resource.title}
                                                </span>
                                                <p className="text-xs text-gray-500">
                                                    {resource.resource_type} {resource.is_required && '• Required'}
                                                </p>
                                            </div>
                                            <span className="text-blue-600">Open →</span>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-4 border-t border-gray-200">
                        {isLocked && (
                            <div className="text-center py-4 text-gray-500">
                                🔒 Complete prerequisites to unlock this item
                            </div>
                        )}

                        {isAvailable && (
                            <button
                                onClick={handleStart}
                                className="w-full px-4 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                            >
                                Start Learning
                            </button>
                        )}

                        {isInProgress && (
                            <div className="space-y-3">
                                {hasKnowledgeCheck && !showQuiz && (
                                    <button
                                        onClick={() => setShowQuiz(true)}
                                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Take Knowledge Check
                                    </button>
                                )}
                                <button
                                    onClick={handleComplete}
                                    className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                >
                                    Mark as Complete
                                </button>
                            </div>
                        )}

                        {isCompleted && (
                            <div className="text-center py-4 text-green-600">
                                ✅ Completed! Great job!
                            </div>
                        )}
                    </div>

                    {hasKnowledgeCheck && showQuiz && (
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {item.knowledgeCheck.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                {item.knowledgeCheck.description}
                            </p>
                            <div className="space-y-4">
                                {item.knowledgeCheck.questions?.map((question, index) => (
                                    <div key={question.id} className="bg-white rounded-lg p-4 border border-gray-200">
                                        <p className="text-sm font-medium text-gray-900 mb-2">
                                            {index + 1}. {question.question}
                                        </p>
                                        <div className="space-y-2">
                                            {question.options?.map((option, optIndex) => (
                                                <label key={optIndex} className="flex items-center space-x-2 text-sm">
                                                    <input
                                                        type={question.type === 'multiple_select' ? 'checkbox' : 'radio'}
                                                        name={`question_${question.id}`}
                                                        value={option}
                                                        onChange={(e) => {
                                                            if (question.type === 'multiple_select') {
                                                                const current = quizAnswers[question.id] || [];
                                                                if (e.target.checked) {
                                                                    setQuizAnswers({
                                                                        ...quizAnswers,
                                                                        [question.id]: [...current, option]
                                                                    });
                                                                } else {
                                                                    setQuizAnswers({
                                                                        ...quizAnswers,
                                                                        [question.id]: current.filter(v => v !== option)
                                                                    });
                                                                }
                                                            } else {
                                                                setQuizAnswers({
                                                                    ...quizAnswers,
                                                                    [question.id]: option
                                                                });
                                                            }
                                                        }}
                                                        className="focus:ring-black"
                                                    />
                                                    <span>{option}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex space-x-3">
                                <button
                                    onClick={handleQuizSubmit}
                                    disabled={submitting}
                                    className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Quiz'}
                                </button>
                                <button
                                    onClick={() => setShowQuiz(false)}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LearningItemView;
